import { AxiosInstance } from "axios";
import TokenHelper from "../utils/tokenHelper";
import { generateApiUrlWithQueryParams } from "../utils/query-params";
import { EventCommunityBootstrap, Member } from "../types";

export type PaginatedAttendees = {
  totalRecords: number;
  records: {
    additionalAttendees: number;
    id: string;
    notification: boolean;
    rsvpStatus: string;
    status: string;
    timestamp: string;
    user: Member;
  }[];
};

// Params to use for the POST /events/:id/invite/users API
export interface CreateEventInviteNoteParams {
  eventInviteNote: {
    body: string;
    closingSalutation: string;
    hostName: string;
    imagePointer: string;
    transformName: string;
    src: string;
  };
  invitees: {
    email: string;
    firstName?: string;
    lastName?: string;
  }[];
}

export const eventSubjectType = "event";

export const basePath = "/events";
// All the paths for this api domain.
export const eventPaths = {
  triggerRsvpDownload: (eventId: number) =>
    basePath + `/${eventId}/rsvps/download`,

  getById: (eventId: number) => basePath + `/${eventId}`,

  getEventStoriesById: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/stories`,
      queryParams
    ),

  toggleEventLike: () => "/luvit",

  getAttendees: (eventId: number, queryParams?: string) =>
    generateApiUrlWithQueryParams(
      basePath + `/${eventId}/attendees`,
      queryParams
    ),

  createInviteNote: (eventId: number) => basePath + `/${eventId}/invite/users`,

  getCommunityBootstrapById: (eventId: number) =>
    basePath + `/${eventId}/community-bootstrap`,
};

class EventApi {
  api: AxiosInstance;
  tokenHelper: TokenHelper;

  constructor(api: AxiosInstance, tokenHelper: TokenHelper) {
    this.api = api;
    this.tokenHelper = tokenHelper;
  }

  // Get event by id.
  // GET /events/<id>
  getById = (eventId: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }

    return this.api.get(eventPaths.getById(eventId), {
      headers,
    });
  };

  // Get event's stories
  // GET /events/<id>/stories
  getEventStoriesById = (eventId: number, queryParams?: string) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }

    return this.api.get(eventPaths.getEventStoriesById(eventId, queryParams), {
      headers,
    });
  };

  // Get event's members (attendees)
  // GET /events/<id>/attendees
  getAttendees = (eventId: number, queryParams?: string) => {
    return this.api.get<PaginatedAttendees>(
      eventPaths.getAttendees(eventId, queryParams)
    );
  };

  // Toggle event like.
  // POST /luvit
  toggleEventLike = (eventId: number) => {
    const token = this.tokenHelper.getRefreshToken();
    return this.api.post(
      eventPaths.toggleEventLike(),
      {
        subjectType: eventSubjectType,
        subjectId: eventId,
      },
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      }
    );
  };

  // Sends a email to the event owner with the rsvp list.
  // GET /events/<id>/rsvp/download
  triggerRsvpDownload = (eventId: number) => {
    const token = this.tokenHelper.getRefreshToken();

    return this.api.get(eventPaths.triggerRsvpDownload(eventId), {
      headers: {
        Authorization: this.tokenHelper.formatAuthHeader(token),
      },
    });
  };

  // Create an Event Invite Note
  // POST /events/:id/invite/users
  createInviteNote = (
    eventId: number,
    inviteNoteParams: CreateEventInviteNoteParams
  ) => {
    const token = this.tokenHelper.getRefreshToken();

    return this.api.post(
      eventPaths.createInviteNote(eventId),
      inviteNoteParams,
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      }
    );
  };

  // Get the event's community and org boostrap
  // GET /events/:id/community-boostrap
  getCommunityBootstrapById = (eventId: number) => {
    return this.api.get<EventCommunityBootstrap>(
      eventPaths.getCommunityBootstrapById(eventId)
    );
  };
}

export default EventApi;
